import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  toasts: Toast[];
  toast: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const toastVariants = {
  default: {
    container: "border-glass-border bg-glass-surface text-foreground",
    icon: "text-foreground",
    title: "text-foreground",
    description: "text-muted-foreground"
  },
  success: {
    container: "border-success-500/20 bg-success-500/10 text-success-700",
    icon: "text-success-600",
    title: "text-success-800",
    description: "text-success-600"
  },
  error: {
    container: "border-error-500/20 bg-error-500/10 text-error-700",
    icon: "text-error-600",
    title: "text-error-800",
    description: "text-error-600"
  },
  warning: {
    container: "border-warning-500/20 bg-warning-500/10 text-warning-700",
    icon: "text-warning-600",
    title: "text-warning-800",
    description: "text-warning-600"
  },
  info: {
    container: "border-primary-500/20 bg-primary-500/10 text-primary-700",
    icon: "text-primary-600",
    title: "text-primary-800",
    description: "text-primary-600"
  }
};

const getIcon = (variant: Toast['variant'] = 'default') => {
  switch (variant) {
    case 'success':
      return <CheckCircle className="h-5 w-5" />;
    case 'error':
      return <AlertCircle className="h-5 w-5" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5" />;
    case 'info':
      return <Info className="h-5 w-5" />;
    default:
      return null;
  }
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const variant = toast.variant || 'default';
  const styles = toastVariants[variant];

  React.useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(toast.id);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onDismiss]);

  return (
    <div
      className={cn(
        "glass-card relative w-full max-w-sm rounded-2xl border p-4 shadow-lg transition-all duration-300 ease-out",
        "animate-in slide-in-from-right-full",
        styles.container
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {getIcon(variant) && (
          <div className={cn("flex-shrink-0", styles.icon)}>
            {getIcon(variant)}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          {toast.title && (
            <div className={cn("font-semibold text-sm mb-1", styles.title)}>
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div className={cn("text-sm", styles.description)}>
              {toast.description}
            </div>
          )}
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="mt-2 text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        
        <button
          onClick={() => onDismiss(toast.id)}
          className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((newToast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...newToast, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Convenience methods for common toast types
export const toast = {
  success: (title: string, description?: string, options?: Partial<Toast>) => {
    // This will be implemented when the hook is used
    console.log('Success toast:', title, description, options);
  },
  error: (title: string, description?: string, options?: Partial<Toast>) => {
    console.log('Error toast:', title, description, options);
  },
  warning: (title: string, description?: string, options?: Partial<Toast>) => {
    console.log('Warning toast:', title, description, options);
  },
  info: (title: string, description?: string, options?: Partial<Toast>) => {
    console.log('Info toast:', title, description, options);
  }
};
